import { html, type TemplateResult } from "lit-html";
import type { AppState } from "../data/store";
import type { BuyerRow } from "../data/types";
import { store } from "../data/store";
import { formatRelative } from "../utils/time";
import { applyAccountFilter, archivedBuyers, dollars, initials } from "../utils/format";
import { iconRestore } from "./icons";

/**
 * Archived buyers cube — soft-archive view (FEST-PHASE-2.P).
 *
 * Lists conversations with archived_at != null. Per-row Restore
 * button clears archived_at and the row re-appears in Buyers /
 * Closed depending on its conv_status.
 */
export function renderArchivedCube(state: AppState): TemplateResult {
  const rows = applyAccountFilter(archivedBuyers(state.buyers), state.selectedAccountId);
  return html`
    <div class="cube cube--archived">
      <div class="cube__head cube-shell__drag">
        <span class="cube__title">Archived</span>
        <span class="cube__count">${rows.length}</span>
      </div>
      <div class="cube__body">
        ${rows.length === 0
          ? html`<div class="empty">No archived buyers. Click the trash on a buyer row or use Archive in bulk to move buyers here.</div>`
          : html`
            <div class="buyers-row buyers-row--head">
              <span></span>
              <span>Buyer</span>
              <span>Account</span>
              <span>Listing</span>
              <span>Status</span>
              <span>Archived</span>
              <span></span>
              <span></span>
            </div>
            ${rows.map((r) => renderArchivedRow(r))}
          `}
      </div>
    </div>
  `;
}

function renderArchivedRow(b: BuyerRow): TemplateResult {
  return html`
    <div class="buyers-row buyers-row--archived">
      <span></span>
      <span class="buyer-name">
        <span class="buyer-avatar buyer-avatar--archived">${initials(b.display_name)}</span>
        <span>${b.display_name}</span>
      </span>
      <span class="buyer-meta">${b.account_username ?? html`<span class="muted">—</span>`}</span>
      <span class="listing-cell">
        <span class="listing-title">${b.listing_title ?? html`<span class="muted">—</span>`}</span>
        ${b.listing_price_cents != null
          ? html`<span class="listing-price">list ${dollars(b.listing_price_cents)}</span>`
          : ""}
      </span>
      <span>${b.conv_status ? html`<span class="pill pill--${b.conv_status}">${b.conv_status}</span>` : ""}</span>
      <span class="time-cell">
        ${b.conv_archived_at ? formatRelative(b.conv_archived_at) : html`<span class="muted">—</span>`}
      </span>
      <span></span>
      <span class="time-cell">
        ${b.conv_id ? html`
          <button class="buyers-row__restore btn btn--small btn--ghost"
                  type="button"
                  title="Restore ${b.display_name}"
                  @click=${(e: Event) => {
                    e.stopPropagation();
                    void store.unarchiveConversation(b.conv_id!);
                  }}>${iconRestore()} Restore</button>
        ` : ""}
      </span>
    </div>
  `;
}
